import React, { useEffect, useRef } from 'react';
import { Form, Table } from 'react-bootstrap';
import { Typography } from 'Components/atoms';
import { ComplianceTrainingModuleOption } from './useComplianceTrainingModules';
import './amplifierTrainingModulesTable.scss';

interface ComplianceTrainingModulesTableProps {
    modules: ComplianceTrainingModuleOption[];
    selectedSubjects: string[];
    onSelectedSubjectsChange: (subjects: string[]) => void;
    minModules: number;
    showValidationError: boolean;
    selectionMode?: 'single' | 'multiple';
}

export const ComplianceTrainingModulesTable: React.FC<ComplianceTrainingModulesTableProps> = ({
    modules,
    selectedSubjects,
    onSelectedSubjectsChange,
    minModules,
    showValidationError,
    selectionMode = 'multiple',
}) => {
    const selectAllRef = useRef<HTMLInputElement>(null);
    const isSingleSelection = selectionMode === 'single';
    const allSubjects = modules.map((module) => module.subject);
    const allSelected = !isSingleSelection && modules.length > 0 && selectedSubjects.length === modules.length;
    const someSelected = !isSingleSelection && selectedSubjects.length > 0 && !allSelected;

    useEffect(() => {
        if (selectAllRef.current) {
            selectAllRef.current.indeterminate = someSelected;
        }
    }, [someSelected]);

    const toggleSubject = (subject: string) => {
        if (isSingleSelection) {
            onSelectedSubjectsChange([subject]);
            return;
        }
        if (selectedSubjects.includes(subject)) {
            onSelectedSubjectsChange(selectedSubjects.filter((item) => item !== subject));
            return;
        }
        onSelectedSubjectsChange([...selectedSubjects, subject]);
    };

    const toggleAll = () => {
        if (isSingleSelection) {
            return;
        }
        if (allSelected) {
            onSelectedSubjectsChange([]);
            return;
        }
        onSelectedSubjectsChange([...allSubjects]);
    };

    const moduleCount = modules?.length ?? 0;

    return (
        <div className="amplifier-training-modules-table d-flex flex-column">
            <div className="amplifier-training-modules-table__container custom-scrollbar">
                <Table hover className="amplifier-training-modules-table__grid mb-0">
                    <thead>
                        <tr>
                            <th className="amplifier-training-modules-table__check-col">
                                <div className="amplifier-training-modules-table__header-select">
                                    {isSingleSelection ? (
                                        <span>Select</span>
                                    ) : (
                                        <>
                                            <Form.Check
                                                type="checkbox"
                                                ref={selectAllRef}
                                                checked={allSelected}
                                                onChange={toggleAll}
                                                aria-label="Select all modules"
                                            />
                                            <span>All</span>
                                        </>
                                    )}
                                </div>
                            </th>
                            <th className="amplifier-training-modules-table__name-col">
                                <div className="amplifier-training-modules-table__module-header">
                                    <span>Module</span>
                                    <span className="amplifier-training-modules-table__selection-count">
                                        {selectedSubjects.length} of {moduleCount} selected
                                    </span>
                                </div>
                            </th>
                            <th>
                                <span>Summary</span>
                                <span className="amplifier-training-modules-table__summary-hint">
                                    (min. {minModules})
                                </span>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {modules.map((module) => {
                            const isSelected = selectedSubjects.includes(module.subject);
                            return (
                                <tr
                                    key={module.subject}
                                    className={
                                        isSelected ? 'amplifier-training-modules-table__row--selected' : undefined
                                    }
                                    onClick={() => toggleSubject(module.subject)}
                                >
                                    <td
                                        className="amplifier-training-modules-table__check-col"
                                        onClick={(event) => event.stopPropagation()}
                                    >
                                        <Form.Check
                                            type={isSingleSelection ? 'radio' : 'checkbox'}
                                            name={
                                                isSingleSelection
                                                    ? 'amplifier-training-single-module-selection'
                                                    : undefined
                                            }
                                            id={`amplifier-training-module-${module.subject}`}
                                            checked={isSelected}
                                            onChange={() => toggleSubject(module.subject)}
                                            aria-label={`${isSingleSelection ? 'Choose' : 'Select'} ${module.name}`}
                                        />
                                    </td>
                                    <td className="amplifier-training-modules-table__name-col">
                                        <Typography variant="body2" color="text-high-emphasis" className="mb-0">
                                            {module.name}
                                        </Typography>
                                    </td>
                                    <td>
                                        <Typography
                                            variant="body3"
                                            color="text-medium-emphasis"
                                            className="amplifier-training-modules-table__summary mb-0"
                                        >
                                            {module.description}
                                        </Typography>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </Table>
            </div>

            {showValidationError && selectedSubjects.length < minModules && (
                <Typography variant="caption4" color="vizualization-red-200">
                    Select at least {minModules} training modules to continue.
                </Typography>
            )}
        </div>
    );
};
